import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Check, ChevronLeft, ChevronRight, Loader2, Scissors, CalendarDays, Clock, User, PartyPopper } from "lucide-react";
import { euro, LOYALTY_EMAIL_KEY } from "../lib/format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAY_SHORT = ["Dom", "Lun", "Mar", "Mer", "Gio", "Ven", "Sab"];
const MONTH_SHORT = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];

const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const addMinutes = (t, mins) => {
  const [h, m] = t.split(":").map(Number);
  const d = new Date(2000, 0, 1, h, m + mins);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

const STEP_META = [
  { n: 1, label: "Servizi", icon: Scissors },
  { n: 2, label: "Data", icon: CalendarDays },
  { n: 3, label: "Orario", icon: Clock },
  { n: 4, label: "I tuoi dati", icon: User },
];

const fadeStep = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -6 },
  transition: { duration: 0.28, ease: [0.16, 1, 0.3, 1] },
};

const Booking = ({ services }) => {
  const cardRef = useRef(null);
  const [step, setStep] = useState(1);
  const [selected, setSelected] = useState([]);
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState(null);
  const [slot, setSlot] = useState(null);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState(() => localStorage.getItem(LOYALTY_EMAIL_KEY) || "");
  const [submitting, setSubmitting] = useState(false);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  const selectedServices = services.filter((s) => selected.includes(s.id));
  const total = selectedServices.reduce((a, s) => a + s.price, 0);
  const emailOk = /^[^@\s]+@[^@\s]+\.[a-z]{2,}$/i.test(email.trim());
  const totalDur = selectedServices.reduce((a, s) => a + s.duration, 0);
  const needed = Math.max(1, Math.ceil(totalDur / 20));

  const days = useMemo(() => {
    const arr = [];
    const d = new Date();
    while (arr.length < 21) {
      if (d.getDay() !== 0) arr.push(new Date(d));
      d.setDate(d.getDate() + 1);
    }
    return arr;
  }, []);

  useEffect(() => {
    if (step === 1) return;
    if (step === 4 && !email) setEmail(localStorage.getItem(LOYALTY_EMAIL_KEY) || "");
    const el = cardRef.current;
    if (!el) return;
    const target = el.getBoundingClientRect().top + window.scrollY - 90;
    if (window.__lenis) window.__lenis.scrollTo(target, { duration: 0.6 });
    else window.scrollTo({ top: target, behavior: "smooth" });
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!date) return;
    setSlotsLoading(true);
    setSlot(null);
    axios
      .get(`${API}/slots`, { params: { date } })
      .then((r) => setSlots(r.data.slots || []))
      .catch(() => {
        setSlots([]);
        toast.error("Impossibile caricare gli orari. Riprova.");
      })
      .finally(() => setSlotsLoading(false));
  }, [date]);

  const toggleService = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const slotAvailable = (time) => {
    if (!slots) return false;
    for (let i = 0; i < needed; i++) {
      const t = addMinutes(time, 20 * i);
      const s = slots.find((x) => x.time === t);
      if (!s || s.remaining <= 0) return false;
    }
    return true;
  };

  const remainingFor = (time) => slots?.find((x) => x.time === time)?.remaining ?? 0;

  const submit = async () => {
    if (!emailOk) {
      toast.error("Controlla l'email.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/bookings`, {
        service_ids: selected,
        date,
        time: slot,
        name: name.trim(),
        phone: phone.trim(),
        email: email.trim(),
      });
      setConfirmed(r.data);
      if (r.data.email) localStorage.setItem(LOYALTY_EMAIL_KEY, r.data.email);
      setStep(5);
      toast.success("Prenotazione confermata! Ti aspettiamo da BANY.");
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(Array.isArray(detail) ? detail.map((item) => item.msg).join(" · ") : detail || "Errore durante la prenotazione. Riprova.");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setStep(1);
    setSelected([]);
    setDate(null);
    setSlot(null);
    setName("");
    setPhone("");
    setEmail(localStorage.getItem(LOYALTY_EMAIL_KEY) || "");
    setConfirmed(null);
  };

  const morning = (slots || []).filter((s) => s.time < "13:00");
  const afternoon = (slots || []).filter((s) => s.time >= "13:00");

  const dateLabel = date
    ? `${DAY_SHORT[new Date(date + "T12:00:00").getDay()]} ${new Date(date + "T12:00:00").getDate()} ${MONTH_SHORT[new Date(date + "T12:00:00").getMonth()]}`
    : "";

  return (
    <section id="prenotazione" data-testid="booking-section" className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto py-20 lg:py-32">
      <motion.div
        initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }} transition={{ duration: 0.6 }}
        className="mb-12"
      >
        <p className="text-xs sm:text-sm uppercase tracking-[0.25em] text-[#0B1F3A] font-mono-alt font-medium mb-4">Prenotazione Online</p>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
          <span className="gold-text">Prenota</span>
        </h2>
      </motion.div>

      <motion.div ref={cardRef} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }} transition={{ duration: 0.6 }} className="rounded-2xl border border-[rgba(11,31,58,0.25)] bg-[#FFFFFF] p-6 sm:p-10 shadow-[0_0_60px_rgba(11,31,58,0.06)] scroll-mt-24">
        {step <= 4 && (
          <div className="flex items-center justify-between mb-10 max-w-lg">
            {STEP_META.map(({ n, label, icon: Icon }) => (
              <div key={n} className="flex flex-col items-center gap-2" data-testid={`booking-step-indicator-${n}`}>
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center border transition-all duration-500 ${
                    step > n
                      ? "bg-[#0B1F3A] border-[#0B1F3A] text-[#F7F2EC]"
                      : step === n
                        ? "border-[#0B1F3A] text-[#0B1F3A] shadow-[0_0_18px_rgba(11,31,58,0.35)]"
                        : "border-[rgba(26,20,22,0.1)] text-[#6F6466]"
                  }`}
                >
                  {step > n ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                </div>
                <span className={`text-[10px] uppercase tracking-[0.15em] ${step >= n ? "text-[#071528]" : "text-[#6F6466]"}`}>{label}</span>
              </div>
            ))}
          </div>
        )}

        <AnimatePresence mode="wait" initial={false}>
          {step === 1 && (
            <motion.div key="s1" {...fadeStep} data-testid="booking-step-services">
              <h3 className="font-display text-xl sm:text-2xl font-semibold mb-6">Cosa facciamo oggi?</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {services.map((s) => {
                  const active = selected.includes(s.id);
                  return (
                    <button
                      key={s.id}
                      data-testid={`service-select-checkbox-${s.id}`}
                      onClick={() => toggleService(s.id)}
                      className={`text-left rounded-xl border p-5 transition-all duration-300 ${
                        active
                          ? "border-[#0B1F3A] bg-[rgba(11,31,58,0.08)] shadow-[0_0_20px_rgba(11,31,58,0.2)]"
                          : "border-[rgba(26,20,22,0.1)] bg-[#FFFFFF] hover:border-[rgba(11,31,58,0.4)]"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-display font-semibold">{s.name}</span>
                        <span className={`w-5 h-5 rounded-full border flex items-center justify-center ${active ? "bg-[#0B1F3A] border-[#0B1F3A]" : "border-[rgba(26,20,22,0.25)]"}`}>
                          {active && <Check className="w-3 h-3 text-[#F7F2EC]" />}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-[#6F6466] font-mono-alt text-xs">{s.duration} min</span>
                        <span className="gold-text font-display font-bold text-lg">€{s.price}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
              <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
                <p data-testid="booking-summary-total-price" className="text-sm text-[#6F6466]">
                  Totale: <span className="gold-text font-display text-2xl font-bold">€{total}</span>
                  <span className="font-mono-alt text-xs ml-3">~{totalDur || 0} min</span>
                </p>
                <button
                  data-testid="booking-step1-continue-button"
                  disabled={selected.length === 0}
                  onClick={() => setStep(2)}
                  className="rounded-full bg-[#0B1F3A] text-[#F7F2EC] px-7 py-3 text-sm font-semibold uppercase tracking-[0.12em] disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#071528] transition-all duration-300 flex items-center gap-2"
                >
                  Continua <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div key="s2" {...fadeStep} data-testid="booking-step-date">
              <h3 className="font-display text-xl sm:text-2xl font-semibold mb-6">Scegli il giorno</h3>
              <div data-testid="booking-date-picker" className="grid grid-cols-4 sm:grid-cols-7 gap-3">
                {days.map((d) => {
                  const iso = toISO(d);
                  const active = date === iso;
                  return (
                    <button
                      key={iso}
                      data-testid={`booking-date-${iso}`}
                      onClick={() => setDate(iso)}
                      className={`relative rounded-xl border py-4 flex flex-col items-center gap-1 transition-all duration-300 ${
                        active
                          ? "border-[#0B1F3A] bg-[rgba(11,31,58,0.1)] shadow-[0_0_20px_rgba(11,31,58,0.2)]"
                          : "border-[rgba(26,20,22,0.1)] bg-[#FFFFFF] hover:border-[rgba(11,31,58,0.4)]"
                      }`}
                    >
                      <span className="text-[10px] uppercase tracking-[0.15em] text-[#6F6466]">{DAY_SHORT[d.getDay()]}</span>
                      <span className={`font-display text-xl font-bold ${active ? "text-[#0B1F3A]" : ""}`}>{d.getDate()}</span>
                      <span className="text-[10px] text-[#6F6466]">{MONTH_SHORT[d.getMonth()]}</span>
                    </button>
                  );
                })}
              </div>
              <div className="mt-8 flex justify-between">
                <button data-testid="booking-step2-back-button" onClick={() => setStep(1)} className="rounded-full border border-[rgba(26,20,22,0.18)] px-6 py-3 text-sm text-[#6F6466] hover:border-[#0B1F3A] hover:text-[#071528] transition-all duration-300 flex items-center gap-2">
                  <ChevronLeft className="w-4 h-4" /> Indietro
                </button>
                <button
                  data-testid="booking-step2-continue-button"
                  disabled={!date}
                  onClick={() => setStep(3)}
                  className="rounded-full bg-[#0B1F3A] text-[#F7F2EC] px-7 py-3 text-sm font-semibold uppercase tracking-[0.12em] disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#071528] transition-all duration-300 flex items-center gap-2"
                >
                  Continua <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          )}

          {step === 3 && (
            <motion.div key="s3" {...fadeStep} data-testid="booking-step-time">
              <h3 className="font-display text-xl sm:text-2xl font-semibold mb-2">Scegli l'orario</h3>
              <p className="text-sm text-[#6F6466] mb-6">{dateLabel} · servizio da ~{totalDur} min</p>
              {slotsLoading ? (
                <div className="flex items-center gap-3 text-[#6F6466] py-10" data-testid="booking-slots-loading">
                  <Loader2 className="w-5 h-5 animate-spin text-[#0B1F3A]" /> Caricamento orari…
                </div>
              ) : !slots || slots.length === 0 ? (
                <p className="text-[#6F6466] py-10" data-testid="booking-slots-empty">Nessuno slot disponibile in questa data. Prova un altro giorno.</p>
              ) : (
                <div className="space-y-8">
                  {[["Mattina", morning], ["Pomeriggio", afternoon]].map(([label, list]) => (
                    <div key={label}>
                      <p className="text-xs uppercase tracking-[0.25em] text-[#0B1F3A] font-mono-alt mb-4">{label}</p>
                      <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-7 gap-3">
                        {list.map((s) => {
                          const ok = slotAvailable(s.time);
                          const active = slot === s.time;
                          return (
                            <button
                              key={s.time}
                              data-testid={`booking-time-slot-${s.time.replace(":", "")}`}
                              aria-pressed={active}
                              disabled={!ok}
                              onClick={() => setSlot(s.time)}
                              className={`rounded-lg border py-3 text-center transition-all duration-300 ${
                                active
                                  ? "border-[#0B1F3A] bg-[#0B1F3A] text-[#F7F2EC] shadow-[0_0_20px_rgba(11,31,58,0.35)]"
                                  : ok
                                    ? "border-[rgba(26,20,22,0.1)] bg-[#FFFFFF] hover:border-[rgba(11,31,58,0.5)]"
                                    : "border-[rgba(26,20,22,0.06)] bg-[#FFFFFF] opacity-30 cursor-not-allowed line-through"
                              }`}
                            >
                              <span className="block font-mono-alt text-sm font-medium">{s.time}</span>
                              {ok && <span className={`block text-[10px] mt-0.5 ${active ? "text-[#F7F2EC]/70" : "text-[#6F6466]"}`}>{remainingFor(s.time)}/2 posti</span>}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-8 flex justify-between">
                <button data-testid="booking-step3-back-button" onClick={() => setStep(2)} className="rounded-full border border-[rgba(26,20,22,0.18)] px-6 py-3 text-sm text-[#6F6466] hover:border-[#0B1F3A] hover:text-[#071528] transition-all duration-300 flex items-center gap-2">
                  <ChevronLeft className="w-4 h-4" /> Indietro
                </button>
                <button
                  data-testid="booking-step3-continue-button"
                  disabled={!slot}
                  onClick={() => setStep(4)}
                  className="rounded-full bg-[#0B1F3A] text-[#F7F2EC] px-7 py-3 text-sm font-semibold uppercase tracking-[0.12em] disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#071528] transition-all duration-300 flex items-center gap-2"
                >
                  Continua <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          )}

          {step === 4 && (
            <motion.div key="s4" {...fadeStep} data-testid="booking-step-details">
              <h3 className="font-display text-xl sm:text-2xl font-semibold mb-6">I tuoi dati</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl">
                <div>
                  <label htmlFor="booking-name" className="block text-xs uppercase tracking-[0.2em] text-[#6F6466] mb-2">Nome</label>
                  <input
                    id="booking-name"
                    data-testid="booking-input-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Il tuo nome"
                    className="w-full rounded-xl border border-[rgba(26,20,22,0.1)] bg-[#FFFFFF] px-5 py-4 text-sm outline-none focus:border-[#0B1F3A] focus:shadow-[0_0_18px_rgba(11,31,58,0.2)] transition-all duration-300 placeholder:text-[#9A8F90]"
                  />
                </div>
                <div>
                  <label htmlFor="booking-phone" className="block text-xs uppercase tracking-[0.2em] text-[#6F6466] mb-2">Telefono</label>
                  <input
                    id="booking-phone"
                    data-testid="booking-input-phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="333 123 4567"
                    type="tel"
                    className="w-full rounded-xl border border-[rgba(26,20,22,0.1)] bg-[#FFFFFF] px-5 py-4 text-sm outline-none focus:border-[#0B1F3A] focus:shadow-[0_0_18px_rgba(11,31,58,0.2)] transition-all duration-300 placeholder:text-[#9A8F90]"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="booking-email" className="block text-xs uppercase tracking-[0.2em] mb-2 text-[#6F6466]">
                    Email obbligatoria <span className="text-[#6F6466] normal-case tracking-normal">
                      — conferma e promemoria
                    </span>
                  </label>
                  <input
                    id="booking-email"
                    data-testid="booking-input-email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="nome@email.it"
                    type="email"
                    required
                    aria-describedby="booking-reminder-info"
                    aria-invalid={email.length > 0 && !emailOk}
                    className="w-full rounded-xl border border-[rgba(26,20,22,0.1)] bg-[#FFFFFF] px-5 py-4 text-sm outline-none transition-all duration-300 placeholder:text-[#9A8F90] focus:border-[#0B1F3A] focus:shadow-[0_0_18px_rgba(11,31,58,0.2)]"
                  />
                  {email.length > 0 && !emailOk && <p data-testid="booking-email-error" role="alert" className="text-xs text-[#C2410C] mt-2">Inserisci un indirizzo email valido.</p>}
                  <p id="booking-reminder-info" data-testid="booking-reminder-info" className="text-xs text-[#6F6466] mt-2">Promemoria circa 24 ore prima. Se prenoti nelle ultime 24 ore, circa 30 minuti prima; se manca meno di mezz'ora, subito.</p>
                  <p data-testid="booking-email-privacy" className="text-[11px] text-[#9A8F90] mt-2">L'email viene usata per la conferma e il promemoria della prenotazione.</p>
                </div>
              </div>
              <div className="mt-6 rounded-xl border border-[rgba(11,31,58,0.25)] bg-[#FFFFFF] p-5 max-w-2xl" data-testid="booking-recap">
                <p className="text-xs uppercase tracking-[0.2em] text-[#0B1F3A] font-mono-alt mb-3">Riepilogo</p>
                <p className="text-sm text-[#1A1416]">{selectedServices.map((s) => s.name).join(" + ")}</p>
                <p className="text-sm text-[#6F6466] mt-1">{dateLabel} · ore {slot}</p>
                <div className="mt-3 pt-3 border-t border-[rgba(26,20,22,0.08)] space-y-1 text-sm">
                  <p className="flex justify-between font-display font-bold text-lg">
                    <span>Totale</span><span data-testid="booking-recap-total" className="gold-text">€{euro(total)}</span>
                  </p>
                </div>
              </div>
              <div className="mt-8 flex flex-col-reverse sm:flex-row justify-between gap-3 max-w-2xl">
                <button data-testid="booking-step4-back-button" onClick={() => setStep(3)} className="rounded-full border border-[rgba(26,20,22,0.18)] px-6 py-3 text-sm text-[#6F6466] hover:border-[#0B1F3A] hover:text-[#071528] transition-all duration-300 flex items-center justify-center gap-2">
                  <ChevronLeft className="w-4 h-4" /> Indietro
                </button>
                <button
                  data-testid="booking-submit-button"
                  disabled={name.trim().length < 2 || phone.trim().length < 6 || !emailOk || submitting}
                  onClick={submit}
                  className="min-w-0 rounded-full bg-[#0B1F3A] text-[#F7F2EC] px-4 sm:px-7 py-3 text-xs sm:text-sm font-semibold uppercase tracking-[0.12em] disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#071528] hover:shadow-[0_0_30px_rgba(11,31,58,0.4)] transition-all duration-300 flex items-center justify-center gap-2"
                >
                  {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  Conferma Prenotazione
                </button>
              </div>
            </motion.div>
          )}

          {step === 5 && confirmed && (
            <motion.div key="s5" {...fadeStep} data-testid="booking-step-confirmation" className="text-center py-8">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 200, damping: 14, delay: 0.15 }}
                className="mx-auto w-20 h-20 rounded-full bg-[#0B1F3A] flex items-center justify-center shadow-[0_0_50px_rgba(11,31,58,0.5)] mb-8"
              >
                <PartyPopper className="w-9 h-9 text-[#F7F2EC]" />
              </motion.div>
              <h3 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-4">
                Ci vediamo presto, <span className="gold-text">{confirmed.name}</span>
              </h3>
              <div data-testid="booking-confirmation-modal" className="mx-auto max-w-md rounded-2xl border border-[rgba(11,31,58,0.3)] bg-[#FFFFFF] p-6 text-left">
                <p className="text-xs uppercase tracking-[0.2em] text-[#6F6466] font-mono-alt mb-1">Codice prenotazione</p>
                <p data-testid="booking-confirmation-code" className="font-mono-alt text-2xl font-semibold text-[#0B1F3A] tracking-[0.2em] mb-4">{confirmed.id}</p>
                <div className="space-y-2 text-sm">
                  <p><span className="text-[#6F6466]">Servizi:</span> {confirmed.service_names.join(" + ")}</p>
                  <p><span className="text-[#6F6466]">Quando:</span> {confirmed.date} alle <span className="text-[#0B1F3A] font-semibold">{confirmed.time}</span></p>
                  <p><span className="text-[#6F6466]">Totale:</span> <span className="gold-text font-display font-bold text-lg">€{euro(confirmed.total)}</span></p>
                  <p data-testid="booking-confirmation-address"><span className="text-[#6F6466]">Dove:</span> Corso Giuseppe Mazzini 25A, Novara</p>
                </div>
              </div>
              <p data-testid="booking-confirmation-email-status" className="text-[#6F6466] text-sm mt-6">{confirmed.customer_email_sent ? `Conferma inviata a ${confirmed.email}. ` : "L'email di conferma non è stata inviata: conserva il codice. "}Con codice e telefono puoi spostare o annullare fino a 24 ore prima nella sezione "Gestisci prenotazione".</p>
              <p data-testid="booking-confirmation-reminder" className="text-[#6F6466] text-sm mt-2">{confirmed.reminder_kind === "immediate" ? "Invio del promemoria previsto subito, perché l'appuntamento è imminente." : confirmed.reminder_kind === "short_notice" ? "Promemoria previsto circa 30 minuti prima dell'appuntamento." : "Promemoria previsto circa 24 ore prima dell'appuntamento."}</p>
              <button
                data-testid="booking-new-button"
                onClick={reset}
                className="mt-6 rounded-full border border-[rgba(11,31,58,0.4)] text-[#071528] px-7 py-3 text-sm uppercase tracking-[0.12em] hover:border-[#0B1F3A] hover:bg-[rgba(11,31,58,0.08)] transition-all duration-300"
              >
                Nuova prenotazione
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </section>
  );
};

export default Booking;
